import { Dispatch, Fragment, ReactNode, SetStateAction, useEffect, useRef, useState } from 'react';
import { useCustomerAPI } from '../../../clients/customer-api/customer-api-client';
import './checkout.scss';
import { OnAccount, OnAccountStatus } from '../../../clients/quipli-service/payment/on-account/on-account';
import { Spinner } from '../../../components/spinner/spinner';
import { Typography } from '../../../components/typography/typography';
import { Badge } from '../../../components/badge/badge';
import classNames from 'classnames';
import { EditableField } from '../../../components/field/field';
import { Button, EcommerceButton } from '../../../components/form/button';
import { ecommerceToast } from '../../../util/toasts';
import StripeSetupInit from '../stripe/setup-component/stripe-setup-init';
import StripeInit from '../stripe/payment-component/stripe-init';
import Icon from '../../../components/icons/icon';
import { ConfirmModal } from '../../../components/modal/confirm-modal';
import { Radio } from '../../../components/radio/radio';
import {
  OnAccountPaymentMethod,
  Payment,
  PaymentMethod,
  PaymentMethodType,
  StripeCard,
  StripePaymentMethod,
} from '../../../clients/quipli-service/payment/payment';
import { Card } from '../../../components/card/card';
import { Theme } from '../../../clients/quipli-service/theme/theme';
import { TabNavV2, TavNabV2Ref } from '../../../components/tab-nav-v2/tab-nav-v2';
import { PaymentMethodRequestV2, StripeBankAccount } from '../../../clients/quipli-service/payment/payment-v2';
import { UserAddress } from '../../../clients/quipli-service/user/user';
import { PaymentIntent, SetupIntent } from '@stripe/stripe-js';
import StripeSetupAuthorization from '../setup-authorization/setup-authorization';

export const SelectPayment = ({
  amount,
  onConfirmPayment,
  setPaymentMethod,
  address,
  setPaymentIntent,
  setPayment,
  mode = 'payment',
}: {
  amount?: number;
  onConfirmPayment?: (payment: PaymentMethod) => void;
  setPaymentMethod?: Dispatch<SetStateAction<PaymentMethodRequestV2 | undefined>>;
  address?: UserAddress;
  setPaymentIntent?: (paymentIntent: PaymentIntent) => void;
  setPayment?: (payment: Payment) => void;
  mode: 'setup' | 'payment';
}) => {
  const { getConfigurations, getMultipleLabels } = useCustomerAPI();
  const [onAccountDescription, setOnAccountDescription] = useState<string>('');
  const [onAccountEnabled, setOnAccountEnabled] = useState<boolean>(false);
  const [paymentComponentEnabled, setPaymentComponentEnabled] = useState<boolean>(false);
  const [achEnabled, setAchEnabled] = useState<boolean>(false);
  const [currency, setCurrency] = useState<string>('usd');

  const tavNavRef = useRef<TavNabV2Ref>(null);
  useEffect(() => {
    getConfigurations(['CONF_RENT_ON_ACCOUNT', 'CONF_ENABLE_AFFIRM', 'CONF_ENABLE_ACH', 'CONF_CURRENCY']).then(
      (conf) => {
        setOnAccountEnabled(
          conf.find((c) => c.variable === 'CONF_RENT_ON_ACCOUNT')?.value === '1' && !setPaymentMethod,
        );
        setPaymentComponentEnabled(conf.find((c) => c.variable === 'CONF_ENABLE_AFFIRM')?.value === '1');
        setAchEnabled(conf.find((c) => c.variable === 'CONF_ENABLE_ACH')?.value === '1');
        setCurrency(conf.find((c) => c.variable === 'CONF_CURRENCY')?.value || 'usd');
      },
    );
    getMultipleLabels(['L_ON_ACCOUNT_REQUEST_DESCRIPTION']).then((labels) =>
      setOnAccountDescription(labels.find((l) => l.key === 'L_ON_ACCOUNT_REQUEST_DESCRIPTION')?.caption || ''),
    );
  }, []);

  const getTabContent = (tab: string) => {
    switch (tab) {
      case 'Credit/debit card':
        return <CardPaymentMethod onConfirmPayment={onConfirmPayment} setPaymentMethod={setPaymentMethod} />;
      case 'Bank Account':
        return <BankPaymentMethod onConfirmPayment={onConfirmPayment} setPaymentMethod={setPaymentMethod} />;
      case 'Bill my account':
        return (
          <BillAccountPaymentMethod
            onConfirmPayment={onConfirmPayment}
            setPaymentMethod={setPaymentMethod}
            description={onAccountDescription}
          />
        );
      case 'Affirm':
        return (
          amount &&
          address &&
          onConfirmPayment && (
            <StripeInit
              amount={amount}
              setPaymentIntent={setPaymentIntent!}
              setPayment={setPayment!}
              onConfirmPayment={onConfirmPayment}
              currency={currency}
              address={address!}
            />
          )
        );
    }
  };

  function PaymentList() {
    const options: string[] = [];
    options.push('Credit/debit card');
    if (achEnabled && mode == 'setup') {
      options.push('Bank Account');
    }
    if (onAccountEnabled) {
      options.push('Bill my account');
    }
    if (paymentComponentEnabled && amount) {
      options.push('Affirm');
    }
    return options;
  }

  return (
    <>
      <div>
        <TabNavV2
          useEcommerceTheme
          ref={tavNavRef}
          tabNames={PaymentList()}
          onSelect={(tab) => <div className="pt-5">{getTabContent(tab)}</div>}
        />
      </div>
    </>
  );
};

const BillAccountPaymentMethod = ({
  onConfirmPayment,
  setPaymentMethod,
  description,
}: {
  onConfirmPayment?: (onAccountPayment: OnAccountPaymentMethod) => void;
  setPaymentMethod?: Dispatch<SetStateAction<PaymentMethodRequestV2 | undefined>>;
  description: string;
}) => {
  const { getUserBankDetails, requestOnAccount } = useCustomerAPI();

  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isRequesting, setIsRequesting] = useState<boolean>(false);
  const [account, setAccount] = useState<OnAccount>();
  const [poNumber, setPoNumber] = useState<string>('');

  const handleRequestAccount = async () => {
    if (isRequesting) return;

    setIsRequesting(true);
    try {
      const res = await requestOnAccount();
      ecommerceToast('Account requested.');
      setIsModalOpen(false);
      setAccount(res);
    } catch (error) {
      console.error(error);
      ecommerceToast('Error requesting account.', 'error');
    } finally {
      setIsRequesting(false);
    }
  };

  const loadBankDetail = async () => {
    setIsLoading(true);
    try {
      const bankDetail = await getUserBankDetails();
      setAccount(bankDetail);
    } catch (error) {
      console.log(error);
    }

    setIsLoading(false);
  };

  useEffect(() => {
    loadBankDetail();
  }, []);

  useEffect(() => {
    if (account) {
      setPaymentMethod?.({
        type: PaymentMethodType.OnAccount,
        account: account,
        poNumber: poNumber,
      });
    }
  }, [account, poNumber]);

  return isLoading ? (
    <Spinner />
  ) : (
    <>
      <div className="px-1 pb-3 text-sm" dangerouslySetInnerHTML={{ __html: description }}></div>

      {account ? (
        <>
          <div className="flex gap-[24px]">
            <div className="flex flex-col gap-[14px] justify-between">
              <Typography type="text-sm" className="text-grey-500">
                Account status
              </Typography>
              <Badge
                text={account.status}
                className={classNames({
                  'bg-success-50 text-success-700': account.status === OnAccountStatus.Approved,
                  'bg-error-50 text-error-700': account.status === OnAccountStatus.Declined,
                })}
              />
            </div>

            <div className="flex flex-col gap-[14px] justify-between">
              <Typography type="text-sm" className="text-grey-500">
                Account number
              </Typography>
              <Typography type="text-sm" className="text-quipli-black-800">
                {account.accountNumber}
              </Typography>
            </div>

            <div className="flex flex-col gap-[14px] justify-between">
              <Typography type="text-sm" className="text-grey-500">
                PO number
              </Typography>

              <EditableField
                type="text"
                label="PO Number"
                noValueText="N/A"
                placeholder="Number"
                value={poNumber}
                onChange={(newValue) => {
                  setPoNumber(newValue);
                }}
              />
              <input type={'hidden'} name={'order_po_number'} value={poNumber} />
            </div>
          </div>

          {onConfirmPayment && (
            <div className="pt-3 flex justify-end">
              <EcommerceButton
                onClick={() => {
                  onConfirmPayment({
                    type: PaymentMethodType.OnAccount,
                    account: account,
                    poNumber: poNumber,
                  });
                }}
              >
                <Typography type="text-sm" weight="medium">
                  Confirm
                </Typography>
              </EcommerceButton>
            </div>
          )}
        </>
      ) : (
        <div>
          <EcommerceButton
            buttonStyle="tertiary"
            onClick={() => {
              setIsModalOpen(true);
            }}
          >
            <Typography type="text-sm" weight="medium">
              Request Account
            </Typography>
          </EcommerceButton>

          <ConfirmModal
            useEcommerceTheme
            isOpen={isModalOpen}
            prompt="Request Account"
            additionalText="You are about to request an account. This will be reviewed by our team."
            noLabel="Cancel"
            yesLabel="Request"
            close={() => !isRequesting && setIsModalOpen(false)}
            onConfirm={handleRequestAccount}
          />
        </div>
      )}
    </>
  );
};

const CardPaymentMethod = ({
  onConfirmPayment,
  setPaymentMethod,
}: {
  onConfirmPayment?: (stripePayment: StripePaymentMethod) => void;
  setPaymentMethod?: Dispatch<SetStateAction<PaymentMethodRequestV2 | undefined>>;
}) => {
  const { getUserCards, deleteUserCard, getActiveTheme } = useCustomerAPI();

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [userCards, setUserCards] = useState<StripeCard[]>([]);
  const [selectionForm, setSelectionForm] = useState<'cardSelect' | 'stripeSetup'>('cardSelect');
  const [deleteConfirmModal, setDeleteConfirmModal] = useState<ReactNode>();
  const [selectedCard, setSelectedCard] = useState<StripeCard>();
  const [theme, setTheme] = useState<Theme>();

  useEffect(() => {
    loadUserCards();
    getActiveTheme().then(setTheme);
  }, []);

  useEffect(() => {
    if (selectedCard) {
      setPaymentMethod?.({
        type: PaymentMethodType.Stripe,
        cardId: selectedCard.id,
        method: selectedCard.cardType,
      });
    }
  }, [selectedCard]);

  const loadUserCards = async () => {
    setIsLoading(true);
    try {
      const cards = (await getUserCards()).filter((uc) => {
        return uc.deletedAt == undefined;
      });
      if (cards.length === 0) {
        setSelectionForm('stripeSetup');
      } else {
        setUserCards(cards);
        setSelectedCard(cards[0]);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOnDelete = async (cardId: number) => {
    try {
      await deleteUserCard(cardId);
      ecommerceToast('Card removed.');
      setDeleteConfirmModal(undefined);
      loadUserCards();
    } catch (error: any) {
      ecommerceToast(error.message || 'Error removing card.', 'error');
    }
  };

  const UserCardTile = ({ card }: { card: StripeCard }) => {
    let icon = undefined;
    switch (card.brand.toLowerCase().trim()) {
      case 'visa':
        icon = <Icon icon={'VisaCardLogo'} size={'medium'}></Icon>;
        break;
      case 'mastercard':
        icon = <Icon icon={'MasterCardLogo'} size={'medium'}></Icon>;
        break;
      case 'amex':
      case 'americanexpress':
        icon = <Icon icon={'AmericanExpressLogo'} size={'medium'}></Icon>;
        break;
    }

    return (
      <Card
        className={classNames(
          'grid items-start relative min-h-[150px] z-[1] hover:border-[var(--primary-color-500)] border-solid border border-grey-300 cursor-pointer',
          {
            'border-[var(--primary-color-500)]': selectedCard?.id === card.id,
            'grid-cols-[1fr_4fr]': onConfirmPayment,
            'grid-cols-[4fr_1fr]': !onConfirmPayment,
          },
        )}
        onClick={() => {
          setSelectedCard(card);
        }}
      >
        {onConfirmPayment && <Radio type="ecommercePrimary" isChecked={selectedCard?.id === card.id} />}

        <div className="flex flex-col gap-2">
          {card.nickname && card.nickname.length > 0 && (
            <Typography type={'text-md'} weight={'bold'}>
              {card.nickname}
            </Typography>
          )}

          <div className="flex flex-col gap-2">
            <div className="flex gap-2 items-center">
              {icon}
              <Typography type={'text-sm'}>•••• {card.last4}</Typography>
            </div>
            <Typography type="text-sm">{card.cardholderName}</Typography>
            <Typography type="text-sm">
              Expiration Date: <span>{`${card.expirationMonth}/${card.expirationYear}`}</span>
            </Typography>
          </div>

          <Typography type="text-sm" className="capitalize">{`Type: ${card.cardType}`}</Typography>

          <div className="flex items-center justify-end absolute w-[72px] h-7 right-2 top-2">
            <Button
              className="icon-button !p-0"
              onClick={() => {
                setDeleteConfirmModal(
                  <ConfirmModal
                    useEcommerceTheme
                    prompt="Remove card?"
                    additionalText="This cannot be undone"
                    noLabel="Cancel"
                    yesLabel="Remove"
                    yesButtonYellow
                    isOpen={true}
                    headerIcon={
                      <div className="flex justify-center">
                        <Icon icon="InfoCircle" size="xlarge" stroke="var(--error-500)" />
                      </div>
                    }
                    onConfirm={() => handleOnDelete(card.id)}
                    close={() => setDeleteConfirmModal(undefined)}
                  />,
                );
              }}
            >
              <Icon icon="Trash" stroke="var(--grey-400)" />
            </Button>
          </div>
        </div>
      </Card>
    );
  };

  const CardSelect = () => (
    <>
      {isLoading ? (
        <Spinner />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 mb-[16px] gap-[16px]">
          <>
            {userCards.map((card) => {
              return (
                <Fragment key={`card-user-title-${card.id}`}>
                  <UserCardTile card={card} />
                </Fragment>
              );
            })}
          </>
        </div>
      )}

      {deleteConfirmModal}

      <div className="flex justify-between">
        <EcommerceButton
          buttonStyle="tertiary"
          onClick={() => {
            setSelectionForm('stripeSetup');
          }}
        >
          <Typography type="text-sm" weight="medium">
            Add New Card
          </Typography>
        </EcommerceButton>

        {selectedCard && onConfirmPayment && (
          <EcommerceButton
            onClick={() => {
              onConfirmPayment({
                type: PaymentMethodType.Stripe,
                card: selectedCard,
              });
            }}
          >
            <Typography type="text-sm" weight="medium">
              Confirm
            </Typography>
          </EcommerceButton>
        )}
      </div>
    </>
  );

  return (
    <>
      {selectionForm == 'cardSelect' && <CardSelect />}
      {selectionForm == 'stripeSetup' && (
        <StripeSetupInit
          color={`#${theme?.primaryColor || 'ffc238'}`}
          enableCancel={true}
          onCancel={() => userCards.length > 0 && setSelectionForm('cardSelect')}
          onSubmit={() => {
            loadUserCards();
            setSelectionForm('cardSelect');
          }}
          type={'card'}
        />
      )}
    </>
  );
};

const BankPaymentMethod = ({
  onConfirmPayment,
  setPaymentMethod,
}: {
  onConfirmPayment?: (stripePayment: StripePaymentMethod) => void;
  setPaymentMethod?: Dispatch<SetStateAction<PaymentMethodRequestV2 | undefined>>;
}) => {
  const { getActiveTheme, searchBankAccounts, deleteBankAccount } = useCustomerAPI();

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [bankAccounts, setBankAccounts] = useState<StripeBankAccount[]>([]);
  const [selectionForm, setSelectionForm] = useState<'bankAccountSelect' | 'stripeSetup'>('bankAccountSelect');
  const [deleteConfirmModal, setDeleteConfirmModal] = useState<ReactNode>();
  const [selectedBankAccount, setSelectedBankAccount] = useState<StripeBankAccount>();
  const [theme, setTheme] = useState<Theme>();

  useEffect(() => {
    loadUserBankAccounts();
    getActiveTheme().then(setTheme);
  }, []);

  useEffect(() => {
    if (selectedBankAccount) {
      setPaymentMethod?.({
        type: PaymentMethodType.Stripe,
        bankAccountId: selectedBankAccount.id,
      });
    }
  }, [selectedBankAccount]);

  const loadUserBankAccounts = async () => {
    setIsLoading(true);
    try {
      const cards = (await searchBankAccounts({})).filter((uc) => {
        return uc.deletedAt == undefined;
      });
      if (cards.length === 0) {
        setSelectionForm('stripeSetup');
      } else {
        setBankAccounts(cards);
        setSelectedBankAccount(cards[0]);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOnDelete = async (bankId: string) => {
    try {
      await deleteBankAccount(bankId);
      ecommerceToast('Account removed.');
      setDeleteConfirmModal(undefined);
      loadUserBankAccounts();
    } catch (error: any) {
      ecommerceToast(error.message || 'Error removing card.', 'error');
    }
  };

  const UserBankAccountTile = ({ bank }: { bank: StripeBankAccount }) => {
    const [showSetupAuthorization, setShowSetupAuthorization] = useState<SetupIntent>();

    return (
      <Card
        className={classNames(
          'grid items-start relative min-h-[150px] z-[1] hover:border-[var(--primary-color-500)] border-solid border border-grey-300 cursor-pointer',
          {
            'border-[var(--primary-color-500)]': selectedBankAccount?.id === bank.id,
            'grid-cols-[1fr_4fr]': onConfirmPayment,
            'grid-cols-[4fr_1fr]': !onConfirmPayment,
          },
        )}
        onClick={() => {
          setSelectedBankAccount(bank);
        }}
      >
        {onConfirmPayment && <Radio type="ecommercePrimary" isChecked={selectedBankAccount?.id === bank.id} />}

        <div className="flex flex-column gap-4">
          {bank.bankName && bank.bankName.length > 0 && (
            <Typography type={'text-md'} weight={'bold'}>
              {bank.bankName}
            </Typography>
          )}

          <div className="flex flex-column gap-2">
            <Typography type={'text-sm'} weight={'semi-bold'}>
              {' '}
              Account ending in {bank.last4}
            </Typography>
            <Typography type="text-sm">{bank.accountHolderName}</Typography>
            <Typography type="text-sm">Status: {bank.status}</Typography>
            {bank.status == 'unverified' &&
            bank.gatewayResponse &&
            (bank.gatewayResponse! as unknown as SetupIntent).status == 'requires_action' ? (
              <EcommerceButton
                buttonStyle="tertiary"
                onClick={() => {
                  setShowSetupAuthorization(bank.gatewayResponse as unknown as SetupIntent);
                }}
              >
                <Typography type="text-sm" weight="medium">
                  Authorize
                </Typography>
              </EcommerceButton>
            ) : (
              <></>
            )}
          </div>

          <div className="flex items-center justify-end absolute w-[72px] h-7 right-2 top-2">
            <Button
              className="icon-button !p-0"
              onClick={() => {
                setDeleteConfirmModal(
                  <ConfirmModal
                    useEcommerceTheme
                    prompt="Remove account?"
                    additionalText="This cannot be undone"
                    noLabel="Cancel"
                    yesLabel="Remove"
                    yesButtonYellow
                    isOpen={true}
                    headerIcon={
                      <div className="flex justify-center">
                        <Icon icon="InfoCircle" size="xlarge" stroke="var(--error-500)" />
                      </div>
                    }
                    onConfirm={() => handleOnDelete(bank.id)}
                    close={() => setDeleteConfirmModal(undefined)}
                  />,
                );
              }}
            >
              <Icon icon="Trash" stroke="var(--grey-400)" />
            </Button>
          </div>
        </div>
        {showSetupAuthorization && <StripeSetupAuthorization gatewayResponse={showSetupAuthorization} />}
      </Card>
    );
  };

  const BankAccountSelect = () => (
    <>
      {isLoading ? (
        <Spinner />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 mb-[16px] gap-[16px]">
          <>
            {bankAccounts.map((card) => {
              return (
                <Fragment key={`card-user-title-${card.id}`}>
                  <UserBankAccountTile bank={card} />
                </Fragment>
              );
            })}
          </>
        </div>
      )}

      {deleteConfirmModal}

      <div className="flex justify-between">
        <EcommerceButton
          buttonStyle="tertiary"
          onClick={() => {
            setSelectionForm('stripeSetup');
          }}
        >
          <Typography type="text-sm" weight="medium">
            Add New Account
          </Typography>
        </EcommerceButton>

        {selectedBankAccount && onConfirmPayment && (
          <EcommerceButton
            onClick={() => {
              onConfirmPayment({
                type: PaymentMethodType.Stripe,
                bankAccount: selectedBankAccount,
              });
            }}
          >
            <Typography type="text-sm" weight="medium">
              Confirm
            </Typography>
          </EcommerceButton>
        )}
      </div>
    </>
  );

  return (
    <>
      {selectionForm == 'bankAccountSelect' && <BankAccountSelect />}
      {selectionForm == 'stripeSetup' && (
        <StripeSetupInit
          color={`#${theme?.primaryColor || 'ffc238'}`}
          enableCancel={true}
          onCancel={() => bankAccounts.length > 0 && setSelectionForm('bankAccountSelect')}
          onSubmit={() => {
            loadUserBankAccounts();
            setSelectionForm('bankAccountSelect');
          }}
          type={'bankAccount'}
        />
      )}
    </>
  );
};
